import type { Instance } from '@popperjs/core';
import {
  preventOverflow,
  offset,
  popperOffsets,
  popperGenerator,
  applyStyles,
  eventListeners,
  computeStyles,
} from '@popperjs/core';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

type Props = {
  anchorEl: Element | null;
  boundaryEl: Element | null;
  children: ReactNode;
  offsetX: number;
  offsetY: number;
  /**
   * Updates popper when a prop changes.
   * UpdateKey is derived from the value that controls positioning of the anchor element.
   * i.e. slider thumb marker.
   */
  updateKey: number | string;
};

const createPopper = popperGenerator({
  defaultOptions: {
    placement: 'top',
    strategy: 'absolute',
  },
  defaultModifiers: [
    eventListeners,
    computeStyles,
    applyStyles,
    popperOffsets,
    preventOverflow,
  ],
});

export const Popper = ({
  anchorEl,
  boundaryEl,
  children,
  offsetX,
  offsetY,
  updateKey,
}: Props) => {
  const popperRef = useRef<Instance | null>(null);

  const cleanUpPopper = () => {
    const popper = popperRef.current;
    if (popper !== null) {
      popper.destroy();
      popperRef.current = null;
    }
  };

  const popperModifiers = useMemo(
    () => [
      {
        ...offset,
        options: {
          offset: [offsetX, offsetY],
        },
      },
      {
        ...preventOverflow,
        options: {
          boundary: boundaryEl,
        },
      },
    ],
    [offsetX, offsetY, boundaryEl]
  );

  const handlePopperRef = useCallback(
    (node: HTMLDivElement) => {
      if (!node || !anchorEl) {
        cleanUpPopper();
        return;
      }
      const currentPopper = popperRef.current;
      if (currentPopper) {
        currentPopper.destroy();
        popperRef.current = null;
      }

      const popper = createPopper(anchorEl, node, {
        modifiers: popperModifiers,
      });
      popperRef.current = popper;
    },
    [anchorEl, popperModifiers]
  );

  useEffect(() => {
    const currentPopper = popperRef.current;
    if (!currentPopper) return;

    currentPopper.setOptions({
      modifiers: popperModifiers,
    });
  }, [popperModifiers]);

  useEffect(() => {
    const currentPopper = popperRef.current;
    if (!currentPopper) return;

    currentPopper.update();
  }, [updateKey]);

  return (
    <div className="absolute">
      <div ref={handlePopperRef}>{children}</div>
    </div>
  );
};
